import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, SpinnerGap, Lightning, Trash, Plus, CalendarBlank,
  Pencil, Check, X, Rows, CalendarCheck, RocketLaunch, Compass,
  BookOpen, ArrowsClockwise, Copy,
} from "@phosphor-icons/react";
import { CONTENT_TYPE_ICONS, WEEK_LABELS, CONTENT_TYPES, StatusBadge, PlanStatusBadge } from "@/components/plan/planConstants";
import { AlwaysIncludeIndicator } from "@/components/AlwaysIncludeIndicator";

export default function PlanBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [activating, setActivating] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ content_type: "LinkedIn Post", platform: "", suggested_topic: "", recommended_week: 1 });
  const [polling, setPolling] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const fetchPlan = useCallback(async () => {
    try {
      const resp = await api.get(`/plans/${id}`);
      setPlan(resp.data);
    } catch {
      toast.error("Plan not found");
      navigate("/plans");
    }
    setLoading(false);
  }, [api, id, navigate]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const planStatus = plan?.status;
  const plannedItems = plan?.planned_items;
  useEffect(() => {
    if (!plan || planStatus !== "active") return;
    const hasPending = (plannedItems || []).some(i => i.status === "planned" && !i.brief_id);
    if (!hasPending) { setPolling(false); return; }
    setPolling(true);
    const interval = setInterval(async () => {
      try {
        const resp = await api.get(`/plans/${id}`);
        setPlan(resp.data);
        const stillPending = (resp.data.planned_items || []).some(i => i.status === "planned" && !i.brief_id);
        if (!stillPending) { setPolling(false); clearInterval(interval); }
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [planStatus, plannedItems, api, id, plan]);

  const handleActivate = async () => {
    setActivating(true);
    try {
      const resp = await api.post(`/plans/${id}/activate`);
      toast.success(resp.data.message || "Plan activated!");
      await fetchPlan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to activate plan");
    }
    setActivating(false);
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api.delete(`/plans/${id}/items/${itemId}`);
      setPlan(prev => ({ ...prev, planned_items: prev.planned_items.filter(i => i.item_id !== itemId) }));
      toast.success("Item removed");
    } catch { toast.error("Failed to remove item"); }
  };

  const handleEditSave = async (itemId) => {
    try {
      await api.patch(`/plans/${id}/items/${itemId}`, editForm);
      setPlan(prev => ({
        ...prev,
        planned_items: prev.planned_items.map(i => i.item_id === itemId ? { ...i, ...editForm } : i),
      }));
      setEditingItem(null);
      toast.success("Item updated");
    } catch { toast.error("Failed to update item"); }
  };

  const handleAddItem = async () => {
    try {
      const resp = await api.post(`/plans/${id}/items`, addForm);
      setPlan(prev => ({ ...prev, planned_items: [...prev.planned_items, resp.data.item] }));
      setShowAddForm(false);
      setAddForm({ content_type: "LinkedIn Post", platform: "", suggested_topic: "", recommended_week: 1 });
      toast.success("Item added");
    } catch { toast.error("Failed to add item"); }
  };

  const startEdit = (item) => {
    setEditingItem(item.item_id);
    setEditForm({
      suggested_topic: item.suggested_topic,
      content_type: item.content_type,
      platform: item.platform,
      recommended_week: item.recommended_week,
    });
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const resp = await api.post(`/plans/${id}/duplicate`, {});
      toast.success("Plan duplicated!");
      navigate(`/plans/${resp.data.plan_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to duplicate plan");
    }
    setDuplicating(false);
  };

  const navigateToBrief = (briefId) => {
    if (briefId) navigate(`/briefs/${briefId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Header />
        <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#FF4400]" /></div>
      </div>
    );
  }

  if (!plan) return null;

  const items = plan.planned_items || [];
  const briefedCount = items.filter(i => i.brief_id).length;
  const generatedCount = items.filter(i => i.status === "generated" || i.status === "published").length;

  const byWeek = [1, 2, 3, 4].map(w => ({
    week: w,
    label: WEEK_LABELS[w - 1],
    items: items.filter(i => i.recommended_week === w).sort((a, b) => a.order - b.order),
  }));

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div className="flex-1 min-w-0">
            <button onClick={() => navigate("/plans")} className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-2" data-testid="plan-builder-back">
              <ArrowLeft size={16} /> All Plans
            </button>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']" data-testid="plan-builder-title">{plan.title}</h1>
              <PlanStatusBadge status={plan.status} />
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1">{items.length} items · {briefedCount} briefed · {generatedCount} generated</p>
            <div className="mt-3"><AlwaysIncludeIndicator /></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-0.5">
              <button onClick={() => setView("list")} className={`p-1.5 rounded-md transition-colors ${view === "list" ? "bg-[#FF4400] text-white" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`} data-testid="view-list-btn" title="List view">
                <Rows size={16} />
              </button>
              <button onClick={() => setView("calendar")} className={`p-1.5 rounded-md transition-colors ${view === "calendar" ? "bg-[#FF4400] text-white" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`} data-testid="view-calendar-btn" title="Calendar view">
                <CalendarCheck size={16} />
              </button>
            </div>
            <Button variant="outline" onClick={handleDuplicate} disabled={duplicating} className="text-sm h-9 px-3 border-[var(--border-strong)] text-[var(--text-secondary)]" data-testid="duplicate-plan-btn">
              {duplicating ? <SpinnerGap size={14} className="animate-spin mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
              Duplicate
            </Button>
            {plan.status === "draft" && (
              <Button onClick={handleActivate} disabled={activating || items.length === 0} className="bg-[#FF4400] hover:bg-[#FF6633] text-white text-sm h-9 px-4" data-testid="activate-plan-btn">
                {activating ? <SpinnerGap size={16} className="animate-spin mr-1.5" /> : <RocketLaunch size={16} className="mr-1.5" />}
                Activate Plan
              </Button>
            )}
          </div>
        </div>

        {plan.ai_strategy_summary && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5 mb-6" data-testid="strategy-summary">
            <div className="flex items-start gap-3">
              <Lightning size={18} className="text-[#FF4400] shrink-0 mt-0.5" weight="fill" />
              <div>
                <h2 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1.5">AI Strategy</h2>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{plan.ai_strategy_summary}</p>
              </div>
            </div>
          </motion.div>
        )}

        {polling && (
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-amber-500/5 border border-amber-500/20 rounded-lg" data-testid="polling-indicator">
            <ArrowsClockwise size={14} className="text-amber-400 animate-spin" />
            <span className="text-xs text-amber-400 font-medium">Generating briefs in the background... items will update automatically</span>
          </div>
        )}

        {plan.goals?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {plan.goals.map((g, i) => (
              <span key={`goal-${g}-${i}`} className="px-2.5 py-1 text-[10px] font-medium rounded-full bg-[#FF4400]/8 text-[#FF4400] border border-[#FF4400]/15">{g}</span>
            ))}
          </div>
        )}

        {plan.status === "draft" && (
          <div className="mb-4">
            {showAddForm ? (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4" data-testid="add-item-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-1 block">Content Type</label>
                    <select value={addForm.content_type} onChange={e => setAddForm(p => ({ ...p, content_type: e.target.value }))} className="w-full h-9 px-3 text-sm rounded-md bg-[var(--bg-base)] border border-[var(--border-strong)] text-[var(--text-primary)]" data-testid="add-item-type">
                      {CONTENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-1 block">Platform</label>
                    <Input value={addForm.platform} onChange={e => setAddForm(p => ({ ...p, platform: e.target.value }))} placeholder="e.g. LinkedIn" className="h-9 text-sm bg-[var(--bg-base)]" data-testid="add-item-platform" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-1 block">Topic</label>
                    <Input value={addForm.suggested_topic} onChange={e => setAddForm(p => ({ ...p, suggested_topic: e.target.value }))} placeholder="What should this piece be about?" className="h-9 text-sm bg-[var(--bg-base)]" data-testid="add-item-topic" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-1 block">Week</label>
                    <div className="flex gap-1.5">
                      {[1, 2, 3, 4].map(w => (
                        <button key={w} onClick={() => setAddForm(p => ({ ...p, recommended_week: w }))} className={`w-9 h-9 rounded-md text-xs font-bold transition-all ${addForm.recommended_week === w ? "bg-[#FF4400] text-white" : "bg-[var(--bg-base)] text-[var(--text-secondary)] border border-[var(--border)]"}`} data-testid={`add-item-week-${w}`}>
                          {w}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={() => setShowAddForm(false)} className="h-10 sm:h-8 text-xs" data-testid="add-item-cancel">Cancel</Button>
                  <Button onClick={handleAddItem} disabled={!addForm.content_type} className="bg-[#FF4400] hover:bg-[#FF6633] text-white h-10 sm:h-8 text-xs" data-testid="add-item-save">Add Item</Button>
                </div>
              </motion.div>
            ) : (
              <button onClick={() => setShowAddForm(true)} className="flex items-center gap-1.5 text-xs font-medium text-[var(--text-muted)] hover:text-[#FF4400] transition-colors" data-testid="add-item-btn">
                <Plus size={14} /> Add content piece
              </button>
            )}
          </div>
        )}

        {view === "list" && (
          <div className="space-y-6" data-testid="plan-list-view">
            {byWeek.map(({ week, label, items: weekItems }) => (
              <div key={week}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">{label}</h3>
                  <span className="text-[10px] text-[var(--text-muted)]">({weekItems.length} items)</span>
                </div>
                {weekItems.length === 0 ? (
                  <p className="text-xs text-[var(--text-muted)] italic pl-2 pb-2">No items this week</p>
                ) : (
                  <div className="space-y-2">
                    {weekItems.map((item, idx) => {
                      const IconComp = CONTENT_TYPE_ICONS[item.content_type] || BookOpen;
                      const isEditing = editingItem === item.item_id;
                      return (
                        <motion.div key={item.item_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                          className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4 hover:border-[var(--border-strong)] transition-colors group" data-testid={`plan-item-${item.item_id}`}>
                          {isEditing ? (
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">Type</label>
                                  <select value={editForm.content_type} onChange={e => setEditForm(p => ({ ...p, content_type: e.target.value }))} className="w-full h-8 px-2 text-xs rounded-md bg-[var(--bg-base)] border border-[var(--border-strong)] text-[var(--text-primary)]" data-testid="edit-item-type">
                                    {CONTENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">Platform</label>
                                  <Input value={editForm.platform} onChange={e => setEditForm(p => ({ ...p, platform: e.target.value }))} className="h-8 text-xs bg-[var(--bg-base)]" data-testid="edit-item-platform" />
                                </div>
                              </div>
                              <div>
                                <label className="text-[10px] font-medium text-[var(--text-muted)] uppercase mb-1 block">Topic</label>
                                <Input value={editForm.suggested_topic} onChange={e => setEditForm(p => ({ ...p, suggested_topic: e.target.value }))} className="h-8 text-xs bg-[var(--bg-base)]" data-testid="edit-item-topic" />
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-[10px] font-medium text-[var(--text-muted)] uppercase">Week:</span>
                                {[1, 2, 3, 4].map(w => (
                                  <button key={w} onClick={() => setEditForm(p => ({ ...p, recommended_week: w }))} className={`w-7 h-7 rounded-md text-[10px] font-bold ${editForm.recommended_week === w ? "bg-[#FF4400] text-white" : "bg-[var(--bg-base)] text-[var(--text-muted)] border border-[var(--border)]"}`}>
                                    {w}
                                  </button>
                                ))}
                              </div>
                              <div className="flex gap-2 pt-1">
                                <Button size="sm" onClick={() => handleEditSave(item.item_id)} className="bg-[#FF4400] hover:bg-[#FF6633] text-white h-10 sm:h-7 text-xs px-3" data-testid="edit-item-save"><Check size={14} className="mr-1" /> Save</Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingItem(null)} className="h-10 sm:h-7 text-xs" data-testid="edit-item-cancel"><X size={14} /></Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-md bg-[var(--bg-base)]">
                                <IconComp size={18} className="text-[var(--text-muted)]" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className="text-xs font-semibold text-[#FF4400]">{item.content_type}</span>
                                  {item.platform && <span className="text-[10px] text-[var(--text-muted)]">· {item.platform}</span>}
                                  <StatusBadge status={item.status} />
                                </div>
                                <p className="text-sm text-[var(--text-primary)] font-medium leading-snug">{item.suggested_topic || "No topic set"}</p>
                                {item.strategic_rationale && (
                                  <p className="text-xs text-[var(--text-muted)] mt-1 leading-relaxed">{item.strategic_rationale}</p>
                                )}
                                {item.brief_id && (
                                  <button onClick={() => navigateToBrief(item.brief_id)} className="mt-2 flex items-center gap-1 text-[10px] font-medium text-emerald-400 hover:text-emerald-300 transition-colors" data-testid={`view-brief-${item.item_id}`}>
                                    <Compass size={12} /> View Brief
                                  </button>
                                )}
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                {plan.status === "draft" && (
                                  <>
                                    <button onClick={() => startEdit(item)} className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" data-testid={`edit-item-${item.item_id}`}>
                                      <Pencil size={14} />
                                    </button>
                                    <button onClick={() => handleDeleteItem(item.item_id)} className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`delete-item-${item.item_id}`}>
                                      <Trash size={14} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {view === "calendar" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="plan-calendar-view">
            {byWeek.map(({ week, label, items: weekItems }) => (
              <motion.div key={week} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: week * 0.06 }}
                className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-[var(--border)] bg-[var(--bg-base)]">
                  <h3 className="text-xs font-bold text-[var(--text-primary)] uppercase tracking-wider">{label}</h3>
                  <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{weekItems.length} pieces</p>
                </div>
                <div className="p-3 space-y-2 min-h-[120px]">
                  {weekItems.length === 0 ? (
                    <p className="text-xs text-[var(--text-muted)] italic text-center py-6">Empty</p>
                  ) : weekItems.map(item => {
                    const IconComp = CONTENT_TYPE_ICONS[item.content_type] || BookOpen;
                    return (
                      <div key={item.item_id} className="p-2.5 rounded-md bg-[var(--bg-base)] border border-[var(--border)] hover:border-[var(--border-strong)] transition-colors" data-testid={`cal-item-${item.item_id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <IconComp size={12} className="text-[#FF4400] shrink-0" />
                          <span className="text-[10px] font-semibold text-[#FF4400] truncate">{item.content_type}</span>
                          <StatusBadge status={item.status} />
                        </div>
                        <p className="text-xs text-[var(--text-primary)] leading-snug line-clamp-2">{item.suggested_topic || "No topic"}</p>
                        {item.brief_id && (
                          <button onClick={() => navigateToBrief(item.brief_id)} className="mt-1.5 text-[9px] font-medium text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1">
                            <Compass size={10} /> Brief ready
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {items.length === 0 && (
          <div className="text-center py-16" data-testid="plan-empty-items">
            <CalendarBlank size={40} className="mx-auto text-[var(--text-muted)] opacity-30 mb-3" weight="duotone" />
            <p className="text-sm text-[var(--text-muted)]">This plan has no items yet. Add some content pieces above.</p>
          </div>
        )}
      </main>
    </div>
  );
}
